type ProductWithCategory = {
  id: number;
  displayOrder?: number;
  categories?: { name: string; displayOrder: number }[];
};

type ProductGroup<T extends ProductWithCategory> = {
  categoryName: string;
  displayOrder: number;
  products: T[];
};

// カテゴリ未設定商品のグループ並び順（カテゴリ側の displayOrder）に最大値を入れておくことで、
// null チェックなしに昇順ソートだけで「その他」グループを末尾に落とせる。
// （null のまま計算すると NaN になり順序が壊れるため最大値を使う）
const UNCATEGORIZED_DISPLAY_ORDER = Number.MAX_SAFE_INTEGER;

export function groupProductsByCategory<T extends ProductWithCategory>(products: T[]): ProductGroup<T>[] {
  // カテゴリ名をキーに、同じカテゴリの商品をまとめていく中間データ
  // 同カテゴリの商品が来たら push、初出なら新規エントリを作成する
  const groups = new Map<string, { displayOrder: number; products: T[] }>();

  for (const product of products) {
    const category = product.categories?.[0]; // 複数カテゴリがあっても先頭のみ使う
    const categoryName = category?.name ?? 'その他';
    const displayOrder = category?.displayOrder ?? UNCATEGORIZED_DISPLAY_ORDER;

    const existing = groups.get(categoryName);
    if (existing) {
      existing.products.push(product);
    } else {
      groups.set(categoryName, { displayOrder, products: [product] });
    }
  }

  return (
    Array.from(groups.entries())
      .map(([categoryName, data]) => ({
        categoryName,
        displayOrder: data.displayOrder,
        // ① グループ内の商品を displayOrder で並べる
        products: [...data.products].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
      }))
      // ② グループ自体をカテゴリの displayOrder で並べる（2段階ソート）
      .sort((a, b) => a.displayOrder - b.displayOrder)
  );
}
